'use client';

import React, { useEffect, useRef, useCallback } from 'react';

const FEATURES = [
  {
    icon: '🔑',
    title: 'JWT + Cookies + Refresh Token Auth',
    desc: 'Access/refresh token pair, automatic rotation, token blacklisting with Redis, and secure httpOnly cookie support.',
    tags: ['jsonwebtoken', 'Redis', 'bcrypt', 'cookies'],
    size: 'large',
    accentColor: '#5B8CFF',
  },
  {
    icon: '💳',
    title: 'Razorpay / Stripe',
    desc: 'Webhook handlers with signature verification, subscription management, and idempotent payment recording.',
    tags: ['Razorpay', 'Stripe', 'Webhooks'],
    size: 'normal',
    accentColor: '#A78BFA',
  },
  {
    icon: '🐳',
    title: 'Docker + Compose',
    desc: 'Multi-stage production Dockerfile, docker-compose with Redis + DB, and health check integration.',
    tags: ['Docker', 'Compose', 'Alpine'],
    size: 'normal',
    accentColor: '#A78BFA',
  },
  {
    icon: '⚙️',
    title: 'BullMQ/RabbitMQ + Redis/Dragonfly Caching Jobs',
    desc: 'Priority queues, delayed jobs, cron scheduling, failed job retries, and a Bull Board dashboard.',
    tags: ['BullMQ', 'RabbitMQ', 'Dragonfly', 'Redis', 'Cron'],
    size: 'large',
    accentColor: '#FF8C42',
  },
  {
    icon: '🗄️',
    title: 'Mongo, PostgreSQL, Neon or any other',
    desc: 'Mongoose ODM for MongoDB or Prisma ORM for PostgreSQL — your choice at scaffold time.',
    tags: ['Mongoose', 'Prisma', 'Neon', 'Migrations'],
    size: 'normal',
    accentColor: '#22D3A0',
  },
  {
    icon: '🛡️',
    title: 'Role-Based Access Control',
    desc: 'Middleware-level RBAC with user, admin, and superadmin roles. Route-level permission guards.',
    tags: ['RBAC', 'Middleware', 'Guards'],
    size: 'normal',
    accentColor: '#FF6B6B',
  },
  {
    icon: '📊',
    title: 'Structured Logging (Pino)',
    desc: 'JSON-structured logs with request IDs, response times, error stacks, and log level filtering.',
    tags: ['Pino', 'pino-http', 'JSON logs'],
    size: 'normal',
    accentColor: '#5B8CFF',
  },
  {
    icon: '🔀',
    title: 'API Versioning + Health/Ping',
    desc: '/api/v1 prefix out of the box, /health, /ping and /ready endpoints, and graceful shutdown handling.',
    tags: ['Versioning', '/health', '/ping', 'Graceful'],
    size: 'large',
    accentColor: '#22D3A0',
  },
];

function FeatureCard({ feature, index }: { feature: (typeof FEATURES)[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)';
  }, []);

  return (
    <div data-component-id="src/app/home/components/FeaturesSection.tsx:95:4" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="95" data-component-end-line="129" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      ref={cardRef}
      className={`animate-on-scroll tilt-card glass-card rounded-2xl p-6 cursor-default ${
        feature.size === 'large' ? 'md:col-span-2' : ''
      }`}
      style={{ transitionDelay: `${index * 50}ms` }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Accent line */}
      <div data-component-id="src/app/home/components/FeaturesSection.tsx:105:6" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="105" data-component-end-line="108" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-px%20mb-5%20rounded%22%7D"
        className="w-12 h-px mb-5 rounded"
        style={{ background: `linear-gradient(90deg, ${feature.accentColor}, transparent)` }}
      />

      <div data-component-id="src/app/home/components/FeaturesSection.tsx:110:6" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="110" data-component-end-line="128" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-4%22%7D" className="flex items-start gap-4">
        <span data-component-id="src/app/home/components/FeaturesSection.tsx:111:8" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="111" data-component-end-line="111" data-component-file="FeaturesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex-shrink-0%20text-2xl%22%7D" className="flex-shrink-0 text-2xl">{feature.icon}</span>
        <div data-component-id="src/app/home/components/FeaturesSection.tsx:112:8" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="112" data-component-end-line="127" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
          <h3 data-component-id="src/app/home/components/FeaturesSection.tsx:113:10" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="113" data-component-end-line="115" data-component-file="FeaturesSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-%5B15px%5D%20font-semibold%20text-%5B%23F0F4FF%5D%20mb-2%20leading-snug%22%7D" className="text-[15px] font-semibold text-[#F0F4FF] mb-2 leading-snug">
            {feature.title}
          </h3>
          <p data-component-id="src/app/home/components/FeaturesSection.tsx:116:10" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="116" data-component-end-line="116" data-component-file="FeaturesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B13px%5D%20text-%5B%238B95B0%5D%20leading-relaxed%20mb-4%22%7D" className="text-[13px] text-[#8B95B0] leading-relaxed mb-4">{feature.desc}</p>
          <div data-component-id="src/app/home/components/FeaturesSection.tsx:117:10" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="117" data-component-end-line="126" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
            {feature.tags.map((tag) => (
              <span data-component-id="src/app/home/components/FeaturesSection.tsx:119:14" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="119" data-component-end-line="124" data-component-file="FeaturesSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B10px%5D%20font-mono%20px-2%20py-0.5%20rounded%20border%20border-%5Brgba(255%2C255%2C255%2C0.07)%5D%20text-%5B%234A5270%5D%20bg-%5Brgba(255%2C255%2C255%2C0.02)%5D%22%7D"
                key={tag}
                className="text-[10px] font-mono px-2 py-0.5 rounded border border-[rgba(255,255,255,0.07)] text-[#4A5270] bg-[rgba(255,255,255,0.02)]"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/home/components/FeaturesSection.tsx:155:4" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="155" data-component-end-line="183" data-component-file="FeaturesSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22features%22%2C%22className%22%3A%22py-28%20px-6%20relative%20bg-%5B%230B0F19%5D%22%7D" id="features" ref={sectionRef} className="py-28 px-6 relative bg-[#0B0F19]">
      <div data-component-id="src/app/home/components/FeaturesSection.tsx:156:6" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="156" data-component-end-line="163" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20overflow-hidden%20pointer-events-none%22%7D" className="absolute inset-0 overflow-hidden pointer-events-none">
        <div data-component-id="src/app/home/components/FeaturesSection.tsx:157:8" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="157" data-component-end-line="162" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-1%2F2%20left-1%2F2%20-translate-x-1%2F2%20-translate-y-1%2F2%20w-%5B800px%5D%20h-%5B800px%5D%20rounded-full%22%7D"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(91,140,255,0.04) 0%, transparent 70%)',
          }}
        />
      </div>

      <div data-component-id="src/app/home/components/FeaturesSection.tsx:165:6" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="165" data-component-end-line="182" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-7xl%22%7D" className="mx-auto max-w-7xl">
        <div data-component-id="src/app/home/components/FeaturesSection.tsx:166:8" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="166" data-component-end-line="175" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-16%20text-center%20animate-on-scroll%22%7D" className="mb-16 text-center animate-on-scroll">
          <div data-component-id="src/app/home/components/FeaturesSection.tsx:167:10" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="167" data-component-end-line="167" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20section-label%22%2C%22textContent%22%3A%22%2F%2F%20Everything%20Included%22%7D" className="mb-4 section-label">// Everything Included</div>
          <h2 data-component-id="src/app/home/components/FeaturesSection.tsx:168:10" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="168" data-component-end-line="170" data-component-file="FeaturesSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-bold%20tracking-tight%20text-%5B%23F0F4FF%5D%20mb-4%22%2C%22textContent%22%3A%228%20production%20modules.%20Zero%20config.%22%7D" className="text-4xl md:text-5xl font-bold tracking-tight text-[#F0F4FF] mb-4">
            8 production modules. Zero config.
          </h2>
          <p data-component-id="src/app/home/components/FeaturesSection.tsx:171:10" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="171" data-component-end-line="174" data-component-file="FeaturesSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%238B95B0%5D%20text-lg%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Every%20module%20is%20battle-tested%2C%20TypeScript-first%2C%20and%20follows%20the%20same%20clean%20architecture%5Cn%20%20%20%20%20%20%20%20%20%20%20%20pattern.%22%7D" className="text-[#8B95B0] text-lg max-w-2xl mx-auto">
            Every module is battle-tested, TypeScript-first, and follows the same clean architecture
            pattern.
          </p>
        </div>

        <div data-component-id="src/app/home/components/FeaturesSection.tsx:177:8" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="177" data-component-end-line="181" data-component-file="FeaturesSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20gap-4%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%22%7D" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature, i) => (
            <FeatureCard data-component-id="src/app/home/components/FeaturesSection.tsx:179:12" data-component-path="src/app/home/components/FeaturesSection.tsx" data-component-line="179" data-component-end-line="179" data-component-file="FeaturesSection.tsx" data-component-name="FeatureCard" data-component-content="%7B%22elementName%22%3A%22FeatureCard%22%7D" key={feature.title} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
